import { VentilationOptions } from './../types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    HttpContentWithData,
    HttpResponse,
    RQAPICallWithData,
} from 'store/APIClient';
import { useDispatch } from 'store/hooks';

import {
    BudgetProposalT,
    BudgetProposalWithResourcesT,
} from 'routes/BudgetModule/config/types';
import { camelize, snakify } from 'helpers/syntaxHelper';
import {
    BudgetProposalsQueryKey,
    createBudgetQueryKeys,
} from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { BudgetPhases } from 'business/constants';
import { useTranslation } from 'react-i18next';
import { ProjectionVentilation } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';

export enum VentilationType {
    PROJECTION_BY = 'projection_by',
    PLURIANNUAL_PROJECTION_BY = 'pluriannual_projection_by',
}

type UpdateProjectionVentilationT = {
    phase: BudgetPhases;
    pluriannualProjectionBy: ProjectionVentilation.SoftwareIds[];
    projectionBy: ProjectionVentilation.SoftwareIds[];
};

export const useGetVentilationOptions = (): {
    data: VentilationOptions | undefined;
    isLoading: boolean;
} =>
    useQuery({
        queryKey: ['ventilation_options'],

        queryFn: async () => {
            const response = await RQAPICallWithData<VentilationOptions>({
                endpoint: 'budget/admin/create_ventilation_options',
                method: 'GET',
            });

            return {
                dimensions: response.content.data.dimensions.map(camelize),
            };
        },
    });

const updateProjectionVentilationCache = (
    oldBudgetProposals:
        | BudgetProposalT[]
        | BudgetProposalWithResourcesT[]
        | undefined,
    data: UpdateProjectionVentilationT,
    bpId: number,
): BudgetProposalT[] | BudgetProposalWithResourcesT[] | undefined => {
    if (!oldBudgetProposals) return oldBudgetProposals;
    return oldBudgetProposals.map((oldBp) =>
        oldBp.id === bpId
            ? {
                  ...oldBp,
                  projectionBy: data.projectionBy.map(
                      camelize,
                  ) as ProjectionVentilation.SoftwareIds[],
                  pluriannualProjectionBy: data.pluriannualProjectionBy.map(
                      camelize,
                  ) as ProjectionVentilation.SoftwareIds[],
              }
            : oldBp,
    );
};
export function useUpdateProjectionVentilationMutation(bpId: number) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { budgetProposalsKeys } = createBudgetQueryKeys();

    const { t } = useTranslation();
    return useMutation({
        mutationFn: ({
            newVentilation,
            ventilationFieldTarget,
        }: {
            newVentilation: ProjectionVentilation.SoftwareIds[];
            ventilationFieldTarget: VentilationType;
        }): Promise<
            HttpResponse<HttpContentWithData<UpdateProjectionVentilationT>>
        > =>
            RQAPICallWithData<UpdateProjectionVentilationT>({
                endpoint: `budget_proposals/${bpId}`,
                method: 'PATCH',
                body: {
                    [ventilationFieldTarget]: newVentilation.map(snakify),
                },
            }),

        onSuccess: ({ content }) => {
            const updateCache = (key: BudgetProposalsQueryKey) =>
                queryClient.setQueryData(
                    key,
                    (
                        oldBudgetProposals:
                            | BudgetProposalWithResourcesT[]
                            | BudgetProposalT[],
                    ) =>
                        updateProjectionVentilationCache(
                            oldBudgetProposals,
                            content.data,
                            bpId,
                        ),
                );
            updateCache(budgetProposalsKeys());
            updateCache(budgetProposalsKeys({ withResources: true }));
            queryClient.resetQueries({
                queryKey: ['atterrissage_projection_details'],
            });
            queryClient.resetQueries({
                queryKey: ['pluriannual_projection_details'],
            });
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.projection.updateSuccess'),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
}
